import { Host, h } from "@stencil/core";
import { getAllShadowRootAncestors } from "../../common/get-all-root-nodes";
import { KEY_CODES, SCROLLABLE_CLASS } from "../../common/reserved-names";
import { SyncWithRAF } from "../../common/sync-with-frames";
import { adoptCommonThemes } from "../../common/theme";
import { forceCSSMinMax, isRTL, subscribeToRTLChanges, unsubscribeToRTLChanges } from "../../common/utils";
import { getDocumentSizes } from "./get-document-sizes";
import { fromPxToNumber, setResponsiveAlignment } from "./utils";
const DRAGGING_CLASS = "gx-popover-dragging";
const POPOVER_PREVENT_FLICKERING_CLASS = "gx-popover-prevent-flickering";
const RESIZING_CLASS = "ch-popover-resizing";
// Custom vars
const POPOVER_ALIGN_BLOCK = "--ch-popover-block";
const POPOVER_ALIGN_INLINE = "--ch-popover-inline";
const POPOVER_ACTION_WIDTH = "--ch-popover-action-width";
const POPOVER_ACTION_HEIGHT = "--ch-popover-action-height";
const POPOVER_ACTION_LEFT = "--ch-popover-action-left";
const POPOVER_ACTION_TOP = "--ch-popover-action-top";
const POPOVER_DRAGGED_X = "--ch-popover-dragged-x";
const POPOVER_DRAGGED_Y = "--ch-popover-dragged-y";
const POPOVER_BLOCK_SIZE = "--ch-popover-block-size";
const POPOVER_INLINE_SIZE = "--ch-popover-inline-size";
const POPOVER_MIN_BLOCK_SIZE = "--ch-popover-min-block-size";
const POPOVER_MIN_INLINE_SIZE = "--ch-popover-min-inline-size";
const POPOVER_MAX_BLOCK_SIZE = "--ch-popover-max-block-size";
const POPOVER_MAX_INLINE_SIZE = "--ch-popover-max-inline-size";
const POPOVER_FORCED_MAX_BLOCK_SIZE = "--ch-popover-forced-max-block-size";
const POPOVER_FORCED_MAX_INLINE_SIZE = "--ch-popover-forced-max-inline-size";
const POPOVER_BORDER_BLOCK_START_SIZE = "--ch-popover-border-block-start-width";
const POPOVER_BORDER_BLOCK_END_SIZE = "--ch-popover-border-block-end-width";
const POPOVER_BORDER_INLINE_START_SIZE = "--ch-popover-border-inline-start-width";
const POPOVER_BORDER_INLINE_END_SIZE = "--ch-popover-border-inline-end-width";
const POPOVER_RTL_CLASS = "ch-popover-rtl";
const POPOVER_RTL = "--ch-popover-rtl";
const POPOVER_RTL_VALUE = "-1";
const PRECISION_TO_AVOID_FLOATING_POINT_ERRORS = 1.5;
const addCursorInDocument = (cursor) => (document.body.style.cursor = cursor);
const resizingCursorDictionary = {
    "block-start": () => addCursorInDocument("ns-resize"),
    "block-end": () => addCursorInDocument("ns-resize"),
    "inline-start": () => addCursorInDocument("ew-resize"),
    "inline-end": () => addCursorInDocument("ew-resize"),
    "block-start-inline-start": rtl => addCursorInDocument(rtl ? "nesw-resize" : "nwse-resize"),
    "block-start-inline-end": rtl => addCursorInDocument(rtl ? "nwse-resize" : "nesw-resize"),
    "block-end-inline-start": rtl => addCursorInDocument(rtl ? "nwse-resize" : "nesw-resize"),
    "block-end-inline-end": rtl => addCursorInDocument(rtl ? "nesw-resize" : "nwse-resize")
};
// Utils
const setProperty = (element, property, value) => element.style.setProperty(property, `${value}px`);
const getProperty = (element, property) => Number((element.style.getPropertyValue(property) || "0px").replace("px", ""));
const addPopoverTargetElement = (actionElement, popoverElement, addAction) => {
    if (actionElement && addAction) {
        actionElement.popoverTargetElement = popoverElement;
    }
};
const removePopoverTargetElement = (actionElement) => {
    if (actionElement) {
        actionElement.popoverTargetElement = undefined;
    }
};
let autoId = 0;
/**
 * The `ch-popover` component represents a popover container that is positioned
 * relative to an element, but placed on the top layer using `position: fixed`.
 */
export class ChPopover {
    constructor() {
        this.resizing = false;
        this.actionById = false;
        this.actionElement = undefined;
        this.allowDrag = "no";
        this.blockAlign = "center";
        this.blockSizeMatch = "content";
        this.closeOnClickOutside = false;
        this.firstLayer = true;
        this.inlineAlign = "center";
        this.inlineSizeMatch = "content";
        this.mode = "auto";
        this.overflowBehavior = "overflow";
        this.positionTry = "none";
        this.resizable = false;
        this.show = false;
    }
    #popoverId = `${autoId++}`;
    // Sync computations with frames
    #borderSizeRAF; // Don't allocate memory until needed when dragging
    #dragRAF; // Don't allocate memory until needed when dragging
    #positionAdjustRAF; // Don't allocate memory until needed
    #resizeRAF; // Don't allocate memory until needed when dragging
    #adjustAlignment = false;
    // Watchers
    #checkPositionWatcher = false;
    #checkBorderSizeWatcher = false;
    #borderSizeObserver;
    #resizeObserver;
    // Drag
    #draggedDistanceX = 0;
    #draggedDistanceY = 0;
    #dragging = false;
    #initialDragEvent;
    #lastDragEvent;
    #isRTLDirection;
    // Resize
    #currentEdge;
    #draggedDistanceXForResize = 0;
    #draggedDistanceYForResize = 0;
    #maxBlockSize = 0;
    #maxInlineSize = 0;
    #minBlockSize = 0;
    #minInlineSize = 0;
    #resizeWasMade = false;
    #resizeByDirectionDictionary = {
        block: (popoverRect, direction) => {
            let currentDraggedDistanceY = this.#lastDragEvent.clientY - this.#initialDragEvent.clientY;
            // Start direction inverts the increment
            if (direction === "start") {
                currentDraggedDistanceY = -currentDraggedDistanceY;
            }
            const newBlockSize = popoverRect.height + currentDraggedDistanceY;
            const newRestrictedBlockSize = forceCSSMinMax(newBlockSize, this.#minBlockSize, this.#maxBlockSize);
            // Do not apply resizes or translations if the control is at the minimum
            // or maximum size
            if (newRestrictedBlockSize === popoverRect.height) {
                return;
            }
            // - - - - - - - - - - - - - DOM write operations - - - - - - - - - - - - -
            // By resizing the start edge the control is translated to improve the UX
            if (direction === "start") {
                this.#draggedDistanceYForResize -= currentDraggedDistanceY;
                setProperty(this.el, POPOVER_DRAGGED_Y, this.#draggedDistanceYForResize);
            }
            setProperty(this.el, POPOVER_BLOCK_SIZE, newRestrictedBlockSize);
        },
        inline: (popoverRect, direction) => {
            let currentDraggedDistanceX = this.#lastDragEvent.clientX - this.#initialDragEvent.clientX;
            if (this.#isRTLDirection) {
                currentDraggedDistanceX = -currentDraggedDistanceX;
            }
            // Start direction inverts the increment
            if (direction === "start") {
                currentDraggedDistanceX = -currentDraggedDistanceX;
            }
            const newInlineSize = popoverRect.width + currentDraggedDistanceX;
            const newRestrictedInlineSize = forceCSSMinMax(newInlineSize, this.#minInlineSize, this.#maxInlineSize);
            // Do not apply resizes or translations if the control is at the minimum
            // or maximum size
            if (newRestrictedInlineSize === popoverRect.width) {
                return;
            }
            // - - - - - - - - - - - - - DOM write operations - - - - - - - - - - - - -
            // By resizing the start edge the control is translated to improve the UX
            if (direction === "start") {
                this.#draggedDistanceXForResize -= currentDraggedDistanceX;
                setProperty(this.el, POPOVER_DRAGGED_X, this.#draggedDistanceXForResize);
            }
            setProperty(this.el, POPOVER_INLINE_SIZE, newRestrictedInlineSize);
        }
    };
    #resizeEdgesAndCornersDictionary = {
        "block-start": popoverRect => this.#resizeByDirectionDictionary.block(popoverRect, "start"),
        "block-end": popoverRect => this.#resizeByDirectionDictionary.block(popoverRect, "end"),
        "inline-start": popoverRect => this.#resizeByDirectionDictionary.inline(popoverRect, "start"),
        "inline-end": popoverRect => this.#resizeByDirectionDictionary.inline(popoverRect, "end"),
        "block-start-inline-start": popoverRect => {
            this.#resizeByDirectionDictionary.block(popoverRect, "start");
            this.#resizeByDirectionDictionary.inline(popoverRect, "start");
        },
        "block-start-inline-end": popoverRect => {
            this.#resizeByDirectionDictionary.block(popoverRect, "start");
            this.#resizeByDirectionDictionary.inline(popoverRect, "end");
        },
        "block-end-inline-start": popoverRect => {
            this.#resizeByDirectionDictionary.block(popoverRect, "end");
            this.#resizeByDirectionDictionary.inline(popoverRect, "start");
        },
        "block-end-inline-end": popoverRect => {
            this.#resizeByDirectionDictionary.block(popoverRect, "end");
            this.#resizeByDirectionDictionary.inline(popoverRect, "end");
        }
    };
    // Refs
    #resizeLayer;
    /**
     * Array that contains all root nodes. It is only computed when the scroll
     * listeners must be attached to track the position of the `ch-popover`.
     *
     * We need to store the root nodes references in an array, since when the
     * element is disconnected from the DOM (`disconnectedCallback` method), the
     * element is no longer attached to its parent elements and thus we can't
     * compute the array.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #rootNodes; // Don't allocate memory until needed when dragging
    el;
    handleActionChange(newActionElement, oldActionElement) {
        // Reset dragged distance
        this.#draggedDistanceX = 0;
        this.#draggedDistanceY = 0;
        // Remove previous action element
        removePopoverTargetElement(oldActionElement);
        addPopoverTargetElement(newActionElement, this.el, !this.actionById);
        // Schedule update for watchers
        this.#checkPositionWatcher = true;
    }
    handleBlockChange() {
        this.#adjustAlignment = true;
    }
    blockSizeWatchChange(newValue) {
        if (this.#resizeWasMade) {
            return;
        }
        // Remove the size constrains
        if (newValue === "content") {
            this.el.style.removeProperty(POPOVER_BLOCK_SIZE);
            this.el.style.removeProperty(POPOVER_MIN_BLOCK_SIZE);
        }
        else if (newValue === "action-element") {
            this.el.style.removeProperty(POPOVER_MIN_BLOCK_SIZE);
        }
        else {
            this.el.style.removeProperty(POPOVER_BLOCK_SIZE);
        }
        // Queue a position adjustment
        this.#adjustAlignment = true;
    }
    handleInlineChange() {
        this.#adjustAlignment = true;
    }
    inlineSizeWatchChange(newValue) {
        if (this.#resizeWasMade) {
            return;
        }
        // Remove the size constrains
        if (newValue === "content") {
            this.el.style.removeProperty(POPOVER_INLINE_SIZE);
            this.el.style.removeProperty(POPOVER_MIN_INLINE_SIZE);
        }
        else if (newValue === "action-element") {
            this.el.style.removeProperty(POPOVER_MIN_INLINE_SIZE);
        }
        else {
            this.el.style.removeProperty(POPOVER_INLINE_SIZE);
        }
        // Queue a position adjustment
        this.#adjustAlignment = true;
    }
    resizableChanged() {
        // Schedule update for border size watcher
        this.#checkBorderSizeWatcher = true;
    }
    showChanged(newShowValue) {
        // Schedule update for watchers
        this.#checkBorderSizeWatcher = true;
        this.#checkPositionWatcher = true;
        // Update the popover visualization
        if (newShowValue) {
            this.#showPopover();
        }
        else {
            if (this.firstLayer) {
                this.#avoidFlickeringInTheNextRender(true);
            }
            this.#hidePopover();
        }
    }
    /**
     * Emitted when the popover is opened by an user interaction.
     *
     * This event can be prevented (`preventDefault()`), interrupting the
     * ch-popover's opening.
     */
    popoverOpened;
    /**
     * Emitted when the popover is closed by an user interaction.
     *
     * This event can be prevented (`preventDefault()`), interrupting the
     * `ch-popover`'s closing.
     *
     * The `reason` property of the event provides more information about
     * the cause of the closing:
     *  - `"click-outside"`: The popover is being closed because the user clicked
     *    outside the popover when using `closeOnClickOutside === true` and
     *    `mode === "manual"`.
     *
     *  - `"escape-key"`: The popover is being closed because the user pressed the
     *    "Escape" key when using `closeOnClickOutside === true` and
     *    `mode === "manual"`.
     *
     *  - `"popover-no-longer-visible"`: The popover is being closed because it
     *    is no longer visible.
     *
     *  - `"toggle"`: The popover is being closed by the native toggle behavior
     *    of popover. It can be produced by the user clicking the `actionElement`,
     *    pressing the "Enter" or "Space" keys on the `actionElement`, pressing
     *    the "Escape" key or other. Used when `mode === "auto"`.
     */
    popoverClosed;
    #showPopover = () => {
        this.el.showPopover();
        this.#addClickOutsideWatcherIfNecessary();
    };
    #hidePopover = () => {
        this.el.hidePopover();
        this.#removeClickOutsideWatcher(); // TODO: Add unit test for this, since it avoid memory leaks
    };
    // TODO: Add unit tests for this feature
    #closePopoverIfNotDefaultPrevented = (reason, event) => {
        const eventInfo = this.popoverClosed.emit(reason);
        if (eventInfo.defaultPrevented) {
            event?.preventDefault();
            return;
        }
        // Only close the popover if the action was not prevented
        this.show = false;
    };
    #handlePopoverCloseOnClickOutside = (event) => {
        const composedPath = event.composedPath();
        if (!composedPath.includes(this.el) &&
            // If the click is triggered on the actionElement, the actionElement must
            // determine if the popover should be closed
            !composedPath.includes(this.actionElement)) {
            this.#closePopoverIfNotDefaultPrevented({ reason: "click-outside" }, event);
        }
    };
    #handlePopoverCloseOnEscapeKey = (event) => {
        if (event.code === KEY_CODES.ESCAPE) {
            this.#closePopoverIfNotDefaultPrevented({ reason: "escape-key" }, event);
        }
    };
    #addClickOutsideWatcherIfNecessary = () => {
        if (this.mode === "manual" && this.closeOnClickOutside) {
            document.addEventListener("click", this.#handlePopoverCloseOnClickOutside, 
            // "capture: true" must be added for the ch-combo-box use case. When
            // the click is triggered on the combo-box, the control prevents the
            // propagation of the event click
            { capture: true, passive: true });
            document.addEventListener("keydown", this.#handlePopoverCloseOnEscapeKey, { capture: true, passive: true });
        }
    };
    #removeClickOutsideWatcher = () => {
        document.removeEventListener("click", this.#handlePopoverCloseOnClickOutside, { capture: true });
        document.removeEventListener("keydown", this.#handlePopoverCloseOnEscapeKey, { capture: true });
    };
    #addDraggingClass = () => {
        if (!this.#dragging) {
            this.el.classList.add(DRAGGING_CLASS);
            this.#dragging = true;
        }
    };
    #removeDraggingClass = () => {
        this.el.classList.remove(DRAGGING_CLASS);
        this.#dragging = false;
    };
    #avoidFlickeringInTheNextRender = (addClass) => {
        if (addClass) {
            // Class to prevent flickering in the first position adjustment
            this.el.classList.add(POPOVER_PREVENT_FLICKERING_CLASS);
        }
        else {
            this.el.classList.remove(POPOVER_PREVENT_FLICKERING_CLASS);
        }
    };
    #setPositionWatcher = () => {
        if (!this.actionElement || !this.show) {
            this.#removePositionWatcher();
            return;
        }
        // If it was observing the previous container, disconnect the observer
        if (this.#resizeObserver) {
            this.#resizeObserver.disconnect();
        }
        // TODO: This memory is never released
        this.#positionAdjustRAF ??= new SyncWithRAF();
        this.#resizeObserver ??= new ResizeObserver((entries) => {
            const popoverWasResized = entries.find(entry => entry.target === this.el);
            // If the popover size is changed, update the alignment in the same
            // frame to avoid any flickering. This optimization avoids an extra
            // setResponsiveAlignment fire and improve the UX when using the
            // combo-box and expanding/collapsing the groups
            if (popoverWasResized) {
                this.#updatePosition();
            }
            else {
                this.#updatePositionRAF();
            }
        });
        this.#resizeObserver.observe(this.actionElement);
        this.#resizeObserver.observe(document.body);
        this.#resizeObserver.observe(this.el);
        // Faster first render. Don't wait until the next animation frame
        this.#updatePosition();
        // The popover's position is now set, so we no longer have to hide it
        if (this.firstLayer) {
            requestAnimationFrame(() => {
                this.#avoidFlickeringInTheNextRender(false);
            });
        }
        // TODO: Add a unit test for this
        // Get all root nodes to attach the scroll listener, since the scroll event
        // does not bubble and therefore, we can't track all scroll events if we}
        // only attach the scroll listener in the document node
        this.#rootNodes ??= getAllShadowRootAncestors(this.el);
        // Listeners
        this.#rootNodes.forEach(rootNode => rootNode.addEventListener("scroll", this.#updatePositionRAF, {
            capture: true,
            passive: true
        }));
        // We must observe the actual viewport size, because observing the
        // document.body does not work when the browser's window is resized
        // and the body has scrollbars.
        // Also, passive mode is not supported in the "resize" event, because this
        // event can not be prevented
        window.addEventListener("resize", this.#updatePositionRAF);
    };
    #updatePositionRAF = () => {
        this.#positionAdjustRAF.perform(this.#updatePosition);
    };
    #updatePosition = () => {
        // - - - - - - - - - - - - - DOM read operations - - - - - - - - - - - - -
        const documentRect = getDocumentSizes();
        const actionRect = this.actionElement.getBoundingClientRect();
        const popoverScrollSizes = {
            width: this.el.scrollWidth,
            height: this.el.scrollHeight
        };
        const computedStyle = getComputedStyle(this.el);
        const actionInlineStart = this.#getActionInlineStartPosition(documentRect, actionRect);
        this.#setResponsiveAlignment(documentRect, actionRect, actionInlineStart, popoverScrollSizes, computedStyle);
        // - - - - - - - - - - - - - DOM write operations - - - - - - - - - - - - -
        setProperty(this.el, POPOVER_ACTION_WIDTH, actionRect.width);
        setProperty(this.el, POPOVER_ACTION_HEIGHT, actionRect.height);
        setProperty(this.el, POPOVER_ACTION_LEFT, actionInlineStart);
        setProperty(this.el, POPOVER_ACTION_TOP, actionRect.top);
    };
    #getActionInlineStartPosition = (documentRect, actionRect) => this.#isRTLDirection
        ? documentRect.width - (actionRect.left + actionRect.width)
        : actionRect.left;
    #setResponsiveAlignment = (documentRect, actionRect, actionInlineStart, popoverScrollSizes, computedStyle) => {
        const popoverWidth = this.#getPopoverInlineSizeAndFixItIfNecessary(actionRect, popoverScrollSizes);
        const popoverHeight = this.#getPopoverBlockSizeAndFixItIfNecessary(actionRect, popoverScrollSizes);
        // TODO: Add e2e tests for this
        // We need to check if a maximum width/height for the popover was defined
        // to give more priority to that value, instead of the actual width/height,
        // even if the width/height comes from "action-element-as-minimum"
        const maxInlineSizeCustomVarValue = computedStyle.getPropertyValue(POPOVER_MAX_INLINE_SIZE);
        const maxBlockSizeCustomVarValue = computedStyle.getPropertyValue(POPOVER_MAX_BLOCK_SIZE);
        let actualPopoverWidth = popoverWidth;
        let actualPopoverHeight = popoverHeight;
        // We only support "px" for the max-block-size and max-inline-size custom
        // var values
        // TODO: Add e2e tests for this
        try {
            if (maxInlineSizeCustomVarValue.endsWith("px")) {
                actualPopoverWidth = Math.min(actualPopoverWidth, Number(maxInlineSizeCustomVarValue.replace("px", "").trim()));
            }
            if (maxBlockSizeCustomVarValue.endsWith("px")) {
                actualPopoverHeight = Math.min(actualPopoverHeight, Number(maxBlockSizeCustomVarValue.replace("px", "").trim()));
            }
        }
        catch {
            //
        }
        const alignment = setResponsiveAlignment(documentRect, actionRect, actionInlineStart, actualPopoverWidth, actualPopoverHeight, computedStyle, this.inlineAlign, this.blockAlign, this.positionTry);
        const inlineOverflow = alignment[0].alignmentOverflow;
        const blockOverflow = alignment[1].alignmentOverflow;
        this.#setOverflowBehavior(actualPopoverWidth, actualPopoverHeight, inlineOverflow, blockOverflow);
        // - - - - - - - - - - - - - DOM write operations - - - - - - - - - - - - -
        setProperty(this.el, POPOVER_ALIGN_INLINE, alignment[0].alignmentPosition);
        setProperty(this.el, POPOVER_ALIGN_BLOCK, alignment[1].alignmentPosition);
    };
    #setOverflowBehavior = (popoverWidth, popoverHeight, inlineOverflow, blockOverflow) => {
        if (this.overflowBehavior !== "add-scroll") {
            return;
        }
        // - - - - - - - - - - - - - DOM read operations - - - - - - - - - - - - -
        const currentMaxInlineSize = getProperty(this.el, POPOVER_FORCED_MAX_INLINE_SIZE);
        const currentMaxBlockSize = getProperty(this.el, POPOVER_FORCED_MAX_BLOCK_SIZE);
        // - - - - - - - - - - - - - DOM write operations - - - - - - - - - - - - -
        // Inline size
        if (inlineOverflow < 0) {
            const newMaxInlineSize = popoverWidth + inlineOverflow;
            // TODO: Add e2e tests for this
            // TODO: We must implement a property to configure the behavior of these
            // kinds of situations
            // Close the popover if it won't be visible.
            if (newMaxInlineSize <= PRECISION_TO_AVOID_FLOATING_POINT_ERRORS) {
                return this.#closePopoverIfNotDefaultPrevented({
                    reason: "popover-no-longer-visible"
                });
            }
            setProperty(this.el, POPOVER_FORCED_MAX_INLINE_SIZE, newMaxInlineSize);
        }
        // Check if the forced inline size is no longer needed
        else if (currentMaxInlineSize &&
            popoverWidth + PRECISION_TO_AVOID_FLOATING_POINT_ERRORS <
                currentMaxInlineSize + inlineOverflow) {
            this.el.style.removeProperty(POPOVER_FORCED_MAX_INLINE_SIZE);
        }
        // Block size
        if (blockOverflow < 0) {
            const newMaxBlockSize = popoverHeight + blockOverflow;
            // TODO: Add e2e tests for this
            // TODO: We must implement a property to configure the behavior of these
            // kinds of situations
            // Close the popover if it won't be visible.
            if (newMaxBlockSize <= PRECISION_TO_AVOID_FLOATING_POINT_ERRORS) {
                return this.#closePopoverIfNotDefaultPrevented({
                    reason: "popover-no-longer-visible"
                });
            }
            setProperty(this.el, POPOVER_FORCED_MAX_BLOCK_SIZE, newMaxBlockSize);
        }
        // Check if the forced block size is no longer needed
        else if (currentMaxBlockSize &&
            popoverHeight + PRECISION_TO_AVOID_FLOATING_POINT_ERRORS <
                currentMaxBlockSize + blockOverflow) {
            this.el.style.removeProperty(POPOVER_FORCED_MAX_BLOCK_SIZE);
        }
    };
    #getPopoverInlineSizeAndFixItIfNecessary = (actionRect, popoverRect) => {
        if (this.inlineSizeMatch === "action-element-as-minimum") {
            setProperty(this.el, POPOVER_MIN_INLINE_SIZE, actionRect.width);
            // TODO: Add e2e tests for this
            return Math.max(actionRect.width, popoverRect.width);
        }
        // Size is determined by the content
        if (this.#resizeWasMade || this.inlineSizeMatch === "content") {
            return popoverRect.width;
        }
        // Size is the same as the `actionElement`
        setProperty(this.el, POPOVER_INLINE_SIZE, actionRect.width);
        return actionRect.width;
    };
    #getPopoverBlockSizeAndFixItIfNecessary = (actionRect, popoverRect) => {
        if (this.blockSizeMatch === "action-element-as-minimum") {
            setProperty(this.el, POPOVER_MIN_BLOCK_SIZE, actionRect.height);
            // TODO: Add e2e tests for this
            return Math.max(actionRect.height, popoverRect.height);
        }
        // Size is determined by the content
        if (this.#resizeWasMade || this.blockSizeMatch === "content") {
            return popoverRect.height;
        }
        // Size is the same as the `actionElement`
        setProperty(this.el, POPOVER_BLOCK_SIZE, actionRect.height);
        return actionRect.height;
    };
    #removePositionWatcher = () => {
        if (this.#resizeObserver) {
            this.#resizeObserver.disconnect();
            this.#resizeObserver = null; // Free the memory
        }
        // TODO: Add a unit test for this
        // Remove listeners in each root node
        this.#rootNodes?.forEach(rootNode => rootNode?.removeEventListener("scroll", this.#updatePositionRAF, {
            capture: true
        }));
        window.removeEventListener("resize", this.#updatePositionRAF);
        // Delete references for root nodes to any avoid memory leak
        this.#rootNodes = undefined;
    };
    #handlePopoverToggle = (event) => {
        const willBeOpen = event.newState === "open";
        let eventInfo;
        // Emit events only when the action is committed by the user
        if (willBeOpen) {
            eventInfo = this.popoverOpened.emit();
        }
        else {
            // If the popover is already closed, don't emit the event again
            // This can happen when:
            //  - The "Escape" key is pressed in mode === "manual"
            //  - The user clicks outside the popover in mode === "manual"
            //  - The show property is changed to false externally
            //  - The user scrolls the window and the popover is no longer visible
            // TODO: Add e2e tests for this
            if (!this.show) {
                return;
            }
            eventInfo = this.popoverClosed.emit({ reason: "toggle" });
        }
        // TODO: Add unit tests for this feature
        if (eventInfo.defaultPrevented) {
            event.preventDefault();
            return;
        }
        // Only open the popover if the action was not prevented
        this.show = willBeOpen;
    };
    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    //                           Drag implementation
    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    #handleMouseDown = (event) => {
        // We should not add preventDefault in this instance, because we would
        // prevent some normal actions like clicking a button or focusing an input
        this.#dragRAF ||= new SyncWithRAF();
        this.#initialDragEvent = event;
        // Add listeners
        document.addEventListener("mousemove", this.#trackElementDragRAF, {
            capture: true
        });
        document.addEventListener("mouseup", this.#handleDragEnd, {
            capture: true,
            passive: true
        });
    };
    #trackElementDragRAF = (event) => {
        this.#dragRAF.perform(this.#trackElementDrag, () => {
            // Improve drag UX by not selecting any button or clicking interactive
            // elements
            event.preventDefault();
            // We remove the pointer-events and user-select properties after the first
            // "mousemove", otherwise double clicking to select text would not work
            this.#addDraggingClass();
            this.#lastDragEvent = event;
        });
    };
    #trackElementDrag = () => {
        // - - - - - - - - - - - - - DOM read operations - - - - - - - - - - - - -
        const currentDraggedDistanceX = this.#lastDragEvent.clientX - this.#initialDragEvent.clientX;
        const currentDraggedDistanceY = this.#lastDragEvent.clientY - this.#initialDragEvent.clientY;
        // - - - - - - - - - - - - - DOM write operations - - - - - - - - - - - - -
        // Update total dragged distance in X
        if (currentDraggedDistanceX !== 0) {
            this.#draggedDistanceX += this.#isRTLDirection // Set dragged distance depending on RTL value
                ? -currentDraggedDistanceX
                : currentDraggedDistanceX;
            setProperty(this.el, POPOVER_DRAGGED_X, this.#draggedDistanceX);
        }
        // Update total dragged distance in Y
        if (currentDraggedDistanceY !== 0) {
            this.#draggedDistanceY += currentDraggedDistanceY;
            setProperty(this.el, POPOVER_DRAGGED_Y, this.#draggedDistanceY);
        }
        // Update last point
        this.#initialDragEvent = this.#lastDragEvent;
    };
    #handleDragEnd = () => {
        // Cancel RAF to prevent access to undefined references
        this.#dragRAF?.cancel();
        // Remove listeners
        document.removeEventListener("mousemove", this.#trackElementDragRAF, {
            capture: true
        });
        document.removeEventListener("mouseup", this.#handleDragEnd, {
            capture: true
        });
        this.#removeDraggingClass();
        // Free the memory
        this.#dragRAF = null;
        this.#initialDragEvent = null;
        this.#lastDragEvent = null;
    };
    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    //                          Resize implementation
    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    #handleEdgeResize = (edge) => (event) => {
        this.resizing = true;
        this.#resizeRAF ||= new SyncWithRAF();
        this.#currentEdge = edge;
        this.#initialDragEvent = event;
        // Specify the cursor for the resize operation. Useful to avoid showing
        // incorrect cursors during resizing
        resizingCursorDictionary[this.#currentEdge](this.#isRTLDirection);
        // Initialize drag variables to improve block-start and inline-start
        // resizing. Otherwise, the popover will always remain in the same X and Y
        // position, even when the block-start or inline-start edges are resized
        this.#draggedDistanceXForResize = this.#draggedDistanceX;
        this.#draggedDistanceYForResize = this.#draggedDistanceY;
        // Get minimum and maximum sizes on first resize operation
        const computedStyle = getComputedStyle(this.el);
        this.#maxBlockSize = fromPxToNumber(computedStyle.maxBlockSize);
        this.#maxInlineSize = fromPxToNumber(computedStyle.maxInlineSize);
        this.#minBlockSize = fromPxToNumber(computedStyle.minBlockSize);
        this.#minInlineSize = fromPxToNumber(computedStyle.minInlineSize);
        // Avoid repositioning the popover
        this.#removePositionWatcher();
        // Avoid watching border changes during the resize
        this.#removeBorderSizeWatcher();
        // Avoid closing the popover during the resize
        this.#removeClickOutsideWatcher();
        // Add listeners
        document.addEventListener("mousemove", this.#trackElementResizeRAF, {
            capture: true
        });
        document.addEventListener("mouseup", this.#handleResizeEnd, {
            capture: true,
            passive: true
        });
    };
    #trackElementResizeRAF = (event) => {
        this.#resizeRAF.perform(this.#trackElementResize, () => {
            // Improve drag UX by not selecting any button or clicking interactive
            // elements
            event.preventDefault();
            // We remove the pointer-events and user-select properties after the first
            // "mousemove", otherwise double clicking to select text would not work
            this.#addDraggingClass();
            this.#lastDragEvent = event;
        });
    };
    #trackElementResize = () => {
        // - - - - - - - - - - - - - DOM read operations - - - - - - - - - - - - -
        const popoverRect = this.el.getBoundingClientRect();
        // - - - - - - - - - - - - - DOM write operations - - - - - - - - - - - - -
        this.#resizeEdgesAndCornersDictionary[this.#currentEdge](popoverRect);
        this.#resizeWasMade = true;
        // Update last point
        this.#initialDragEvent = this.#lastDragEvent;
    };
    #handleResizeEnd = () => {
        this.resizing = false;
        // Cancel RAF to prevent access to undefined references
        this.#resizeRAF?.cancel();
        // Reset document cursor back to normal
        document.body.style.cursor = null;
        // Reset dragged distance to its original value
        setProperty(this.el, POPOVER_DRAGGED_X, this.#draggedDistanceX);
        setProperty(this.el, POPOVER_DRAGGED_Y, this.#draggedDistanceY);
        // Update the position of the popover when the resize ends
        this.#setPositionWatcher();
        // Start again watching border size changes
        this.#setBorderSizeWatcher();
        // Add again the click outside watcher if necessary. RAF is needed to
        // prevent the popover from closing, since the document click event will be
        // dispatched after the execution of this function (mouseup handler)
        requestAnimationFrame(this.#addClickOutsideWatcherIfNecessary);
        // Remove listeners
        document.removeEventListener("mousemove", this.#trackElementResizeRAF, {
            capture: true
        });
        document.removeEventListener("mouseup", this.#handleResizeEnd, {
            capture: true
        });
        this.#removeDraggingClass();
        // Free the memory
        this.#resizeRAF = null;
        this.#initialDragEvent = null;
        this.#lastDragEvent = null;
    };
    /**
     * This observer watches the size of each border in the control to adjust the
     * position of the invisible resize elements (edges and corners).
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #setBorderSizeWatcher = () => {
        if (!this.resizable || !this.show) {
            this.#removeBorderSizeWatcher();
            return;
        }
        this.#borderSizeRAF ??= new SyncWithRAF();
        this.#borderSizeObserver = new ResizeObserver(this.#updateBorderSizeRAF);
        // Observe the size of the edges to know if the border
        this.#borderSizeObserver.observe(this.el, { box: "border-box" });
        this.#borderSizeObserver.observe(this.#resizeLayer);
    };
    #updateBorderSizeRAF = () => {
        this.#borderSizeRAF.perform(this.#updateBorderSize);
    };
    #updateBorderSize = () => {
        // - - - - - - - - - - - - - DOM read operations - - - - - - - - - - - - -
        const computedStyle = getComputedStyle(this.el);
        // - - - - - - - - - - - - - DOM write operations - - - - - - - - - - - - -
        this.el.style.setProperty(POPOVER_BORDER_BLOCK_START_SIZE, computedStyle.borderBlockStartWidth);
        this.el.style.setProperty(POPOVER_BORDER_BLOCK_END_SIZE, computedStyle.borderBlockEndWidth);
        this.el.style.setProperty(POPOVER_BORDER_INLINE_START_SIZE, computedStyle.borderInlineStartWidth);
        this.el.style.setProperty(POPOVER_BORDER_INLINE_END_SIZE, computedStyle.borderInlineEndWidth);
    };
    #removeBorderSizeWatcher = () => {
        if (this.#borderSizeObserver) {
            this.#borderSizeObserver.disconnect();
            this.#borderSizeObserver = null; // Free the memory
        }
        // TODO: Add a unit test to ensure the queued RAF is canceled
        this.#borderSizeRAF?.cancel();
        this.#borderSizeRAF = null; // Free the memory
    };
    #updatePositionWithRTL = (rtl) => {
        this.#isRTLDirection = rtl;
        if (rtl) {
            this.el.style.setProperty(POPOVER_RTL, POPOVER_RTL_VALUE);
            this.el.classList.add(POPOVER_RTL_CLASS);
        }
        else {
            this.el.style.removeProperty(POPOVER_RTL);
            this.el.classList.remove(POPOVER_RTL_CLASS);
        }
    };
    connectedCallback() {
        adoptCommonThemes(this.el.shadowRoot.adoptedStyleSheets);
        // Set RTL watcher
        subscribeToRTLChanges(this.#popoverId, this.#updatePositionWithRTL);
        // Initialize RTL position
        this.#updatePositionWithRTL(isRTL());
        if (this.firstLayer) {
            this.#avoidFlickeringInTheNextRender(true);
        }
    }
    componentWillRender() {
        if (this.#checkPositionWatcher) {
            this.#checkPositionWatcher = false;
            // Update watchers
            this.#setPositionWatcher();
        }
        if (this.#checkBorderSizeWatcher) {
            this.#checkBorderSizeWatcher = false;
            // Wait until the resize edges have been rendered
            requestAnimationFrame(() => {
                this.#setBorderSizeWatcher();
            });
        }
        if (this.#adjustAlignment) {
            const documentRect = getDocumentSizes();
            const actionRect = this.actionElement.getBoundingClientRect();
            const popoverScrollSizes = {
                width: this.el.scrollWidth,
                height: this.el.scrollHeight
            };
            const computedStyle = getComputedStyle(this.el);
            const actionInlineStart = this.#getActionInlineStartPosition(documentRect, actionRect);
            this.#setResponsiveAlignment(documentRect, actionRect, actionInlineStart, popoverScrollSizes, computedStyle);
        }
    }
    componentDidLoad() {
        // Initialize popoverTargetElement
        addPopoverTargetElement(this.actionElement, this.el, !this.actionById);
        // Initialize watchers
        this.#setPositionWatcher();
        this.#setBorderSizeWatcher();
        if (this.show) {
            this.#showPopover();
        }
    }
    disconnectedCallback() {
        this.#removePositionWatcher();
        this.#removeBorderSizeWatcher();
        // If the action element still exists, remove the reference
        removePopoverTargetElement(this.actionElement);
        // Defensive programming. Make sure the document does not have any unwanted handler
        this.#handleDragEnd();
        // Avoid leaving handlers in the document
        this.#removeClickOutsideWatcher(); // TODO: Add unit test for this, since it avoid memory leaks
        // Disconnect RTL watcher to avoid memory leaks
        unsubscribeToRTLChanges(this.#popoverId);
    }
    render() {
        const canAddListeners = this.show;
        return (h(Host, { key: 'f8aaf3118ed735297e810f72eb8be9f4c088083d', class: {
                "gx-popover-header-drag": canAddListeners && this.allowDrag === "header",
                [RESIZING_CLASS]: this.resizing,
                [SCROLLABLE_CLASS]: this.overflowBehavior === "add-scroll"
            }, popover: this.mode, onMouseDown: canAddListeners && this.allowDrag === "box"
                ? this.#handleMouseDown
                : null,
            // TODO: Add beforetoggle listener to properly support preventing the natural toggle
            // TODO: Should we add this event with popover="manual"???
            // TODO: Check if the actionElement is an instance of Button to add this handler
            onToggle: this.#handlePopoverToggle }, this.allowDrag === "header" && (h("div", { key: 'd161a225963a1147ac2daaded3531715b4f875a4', class: "header", part: "header", onMouseDown: canAddListeners ? this.#handleMouseDown : null }, h("slot", { key: '3d680f1176c58aff87db6a3a4298de41ae5ca14e', name: "header" }))), h("slot", { key: 'f762e3a226c414b55d36fde4b3f7f6b198e23111' }), this.resizable &&
            this.show && [
            h("div", { key: '94555738b81acb7ad4f69aa6ac5c1fbd6a1470b0', class: "edge__block-start", onMouseDown: this.#handleEdgeResize("block-start") }), // Top
            h("div", { key: 'c7dcbda0cba5429b70d36ab2e1e5c35247608e50', class: "edge__inline-end", onMouseDown: this.#handleEdgeResize("inline-end") }), // Right
            h("div", { key: '68d12b14c8944e4dbfe12078b936daefae343a34', class: "edge__block-end", onMouseDown: this.#handleEdgeResize("block-end") }), // Bottom
            h("div", { key: '2daa8d18a452a8f042f6f429da8947976a22c777', class: "edge__inline-start", onMouseDown: this.#handleEdgeResize("inline-start") }), // Left
            h("div", { key: 'f252c4bb7a010a7cdce408b89297efd5927c9449', class: "corner__block-start-inline-start", onMouseDown: this.#handleEdgeResize("block-start-inline-start") }), // Top Left
            h("div", { key: 'a886c43e0fa08459fd0b1d1166cc59e9d51b5d1c', class: "corner__block-start-inline-end", onMouseDown: this.#handleEdgeResize("block-start-inline-end") }), // Top Right
            h("div", { key: '2ae345828dd22a0e801f4abb842e1da4e4db730a', class: "corner__block-end-inline-start", onMouseDown: this.#handleEdgeResize("block-end-inline-start") }), // Bottom Left
            h("div", { key: '019a776823dd43ca0c4e64b8eeaf98b9776d6784', class: "corner__block-end-inline-end", onMouseDown: this.#handleEdgeResize("block-end-inline-end") }), // Bottom Right
            h("div", { key: 'e8e57fb4731fca939289eaa19ac36cfa4a683b6b', class: "resize-layer", ref: el => (this.#resizeLayer = el) })
        ]));
    }
    static get is() { return "ch-popover"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["popover.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["popover.css"]
        };
    }
    static get properties() {
        return {
            "actionById": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` if the `actionElement` binds the ch-popover using an external ID.\nIf so, the `popoverTargetElement` property won't be configured in the\naction element."
                },
                "attribute": "action-by-id",
                "reflect": false,
                "defaultValue": "false"
            },
            "actionElement": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "PopoverActionElement",
                    "resolved": "HTMLButtonElement | HTMLInputElement",
                    "references": {
                        "PopoverActionElement": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/popover/types.ts::PopoverActionElement"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies a reference of the action that controls the popover control."
                }
            },
            "allowDrag": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"box\" | \"header\" | \"no\"",
                    "resolved": "\"box\" | \"header\" | \"no\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the drag behavior of the popover.\nIf `allowDrag === \"header\"`, a slot with the `\"header\"` name will be\navailable to place the header content."
                },
                "attribute": "allow-drag",
                "reflect": false,
                "defaultValue": "\"no\""
            },
            "blockAlign": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChPopoverAlign",
                    "resolved": "\"center\" | \"inside-end\" | \"inside-start\" | \"outside-end\" | \"outside-start\"",
                    "references": {
                        "ChPopoverAlign": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/popover/types.ts::ChPopoverAlign"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the block alignment of the window."
                },
                "attribute": "block-align",
                "reflect": false,
                "defaultValue": "\"center\""
            },
            "blockSizeMatch": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChPopoverSizeMatch",
                    "resolved": "\"action-element\" | \"action-element-as-minimum\" | \"content\"",
                    "references": {
                        "ChPopoverSizeMatch": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/popover/types.ts::ChPopoverSizeMatch"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies how the popover adapts its block size.\n - \"content\": The block size of the control will be determined by its\n   content block size.\n - \"action-element\": The block size of the control will match the block\n   size of the `actionElement`.\n - \"action-element-as-minimum\": The minimum block size of the control\n   will match the block size of the `actionElement`.\n\nIf the control is resized at runtime, only the \"action-element-as-minimum\"\nvalue will still work."
                },
                "attribute": "block-size-match",
                "reflect": false,
                "defaultValue": "\"content\""
            },
            "closeOnClickOutside": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property only applies for `\"manual\"` mode. In native popovers, when\nusing `\"manual\"` mode the popover doesn't close when clicking outside the\ncontrol. This property allows to close the popover when clicking outside\nin `\"manual\"` mode.\nWith this, the popover will close if the click is triggered on any other\nelement than the popover and the `actionElement`. It will also close if\nthe \"Escape\" key is pressed."
                },
                "attribute": "close-on-click-outside",
                "reflect": false,
                "defaultValue": "false"
            },
            "firstLayer": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` if the control is not stacked with another top layer."
                },
                "attribute": "first-layer",
                "reflect": false,
                "defaultValue": "true"
            },
            "inlineAlign": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChPopoverAlign",
                    "resolved": "\"center\" | \"inside-end\" | \"inside-start\" | \"outside-end\" | \"outside-start\"",
                    "references": {
                        "ChPopoverAlign": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/popover/types.ts::ChPopoverAlign"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the inline alignment of the window."
                },
                "attribute": "inline-align",
                "reflect": false,
                "defaultValue": "\"center\""
            },
            "inlineSizeMatch": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChPopoverSizeMatch",
                    "resolved": "\"action-element\" | \"action-element-as-minimum\" | \"content\"",
                    "references": {
                        "ChPopoverSizeMatch": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/popover/types.ts::ChPopoverSizeMatch"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies how the popover adapts its inline size.\n - \"content\": The inline size of the control will be determined by its\n   content inline size.\n - \"action-element\": The inline size of the control will match the inline\n   size of the `actionElement`.\n - \"action-element-as-minimum\": The minimum inline size of the control\n   will match the inline size of the `actionElement`.\n\nIf the control is resized at runtime, only the \"action-element-as-minimum\"\nvalue will still work."
                },
                "attribute": "inline-size-match",
                "reflect": false,
                "defaultValue": "\"content\""
            },
            "mode": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"auto\" | \"manual\"",
                    "resolved": "\"auto\" | \"manual\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Popovers that have the `\"auto\"` state can be \"light dismissed\" by\nselecting outside the popover area, and generally only allow one popover\nto be displayed on-screen at a time. By contrast, `\"manual\"` popovers must\nalways be explicitly hidden, but allow for use cases such as nested\npopovers in menus."
                },
                "attribute": "popover",
                "reflect": false,
                "defaultValue": "\"auto\""
            },
            "overflowBehavior": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "| \"overflow\"\n    | \"add-scroll\"",
                    "resolved": "\"add-scroll\" | \"overflow\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies how the popover behaves when the content overflows the window\nsize.\n  - \"overflow\": The control won't implement any behavior if the content overflows.\n  - \"add-scroll\": The control will place a scroll if the content overflows."
                },
                "attribute": "overflow-behavior",
                "reflect": true,
                "defaultValue": "\"overflow\""
            },
            "positionTry": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"flip-block\" | \"flip-inline\" | \"none\"",
                    "resolved": "\"flip-block\" | \"flip-inline\" | \"none\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies an alternative position to try when the control overflows the\nwindow."
                },
                "attribute": "position-try",
                "reflect": false,
                "defaultValue": "\"none\""
            },
            "resizable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies whether the control can be resized. If `true` the control can be\nresized at runtime by dragging the edges or corners."
                },
                "attribute": "resizable",
                "reflect": false,
                "defaultValue": "false"
            },
            "show": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies whether the popover is hidden or visible."
                },
                "attribute": "show",
                "reflect": true,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "resizing": {}
        };
    }
    static get events() {
        return [{
                "method": "popoverOpened",
                "name": "popoverOpened",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the popover is opened by an user interaction.\n\nThis event can be prevented (`preventDefault()`), interrupting the\nch-popover's opening."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "popoverClosed",
                "name": "popoverClosed",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the popover is closed by an user interaction.\n\nThis event can be prevented (`preventDefault()`), interrupting the\n`ch-popover`'s closing.\n\nThe `reason` property of the event provides more information about\nthe cause of the closing:\n - `\"click-outside\"`: The popover is being closed because the user clicked\n   outside the popover when using `closeOnClickOutside === true` and\n   `mode === \"manual\"`.\n\n - `\"escape-key\"`: The popover is being closed because the user pressed the\n   \"Escape\" key when using `closeOnClickOutside === true` and\n   `mode === \"manual\"`.\n\n - `\"popover-no-longer-visible\"`: The popover is being closed because it\n   is no longer visible.\n\n - `\"toggle\"`: The popover is being closed by the native toggle behavior\n   of popover. It can be produced by the user clicking the `actionElement`,\n   pressing the \"Enter\" or \"Space\" keys on the `actionElement`, pressing\n   the \"Escape\" key or other. Used when `mode === \"auto\"`."
                },
                "complexType": {
                    "original": "PopoverClosedInfo",
                    "resolved": "{ reason: \"click-outside\" | \"escape-key\" | \"popover-no-longer-visible\" | \"toggle\"; }",
                    "references": {
                        "PopoverClosedInfo": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/popover/types.ts::PopoverClosedInfo"
                        }
                    }
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "actionElement",
                "methodName": "handleActionChange"
            }, {
                "propName": "blockAlign",
                "methodName": "handleBlockChange"
            }, {
                "propName": "blockSizeMatch",
                "methodName": "blockSizeWatchChange"
            }, {
                "propName": "inlineAlign",
                "methodName": "handleInlineChange"
            }, {
                "propName": "inlineSizeMatch",
                "methodName": "inlineSizeWatchChange"
            }, {
                "propName": "resizable",
                "methodName": "resizableChanged"
            }, {
                "propName": "show",
                "methodName": "showChanged"
            }];
    }
}
